// @flow

/**
 * Layout is what Masonry uses to keep track of which grid items have been
 * measured and laid out. Layouts should be treated as readonly.
 */

export type ItemConstraints = {|
  width?: number,
  minWidth?: number,
  maxWidth?: number,
  height?: number,
  minHeight?: number,
  maxHeight?: number,
|};

export type Size = {|
  width: number,
  height: number,
|};

export type Position = {|
  left: number,
  top: number,
|};

export type ItemLayoutInfo = {|
  size?: Size,
  position?: Position,
  constraints?: ItemConstraints,
  customIndex?: number,
|};

// Layouts describe a list of items and how they should be laid out. Layouts
// should be treated as readonly, and modifications are made by creating a clone
// with changes merged in.
export default class Layout<ItemType> {
  items: Array<ItemType> = [];

  getItemLayoutInfo: (key: ItemType) => ?ItemLayoutInfo;

  constructor(
    items: Array<ItemType> = [],
    getItemLayoutInfo: (key: ItemType) => ?ItemLayoutInfo = () => null,
  ) {
    this.items = items;
    this.getItemLayoutInfo = getItemLayoutInfo;
  }

  // Size refers to the smallest containing box that holds all layout content
  // (excluding any items that still need measurement).
  size: ?Size;

  // contentFilledSize refers to the largest area that is fully filled with
  // content. With layouts such as the usual Masonry grid layout, the last items
  // in the list may have uneven heights and will create a "sawtooth" effect. In
  // this case contentFilledSize will measure the bottom of the shortest column.
  contentFilledSize: ?Size;

  // A layout is considered to be "measuring" if any of its items still need
  // measuring. When rendered, LayoutRenderer will render unmeasured content
  // offscreen and will create a new layout with the updated sizes.
  isMeasuring: ?boolean;
}
